import { useEffect, useState, useCallback } from "react";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { ReactComponent as CubeIcon } from "../../assets/cube.icon.svg";
import { ReactComponent as CloudErrorIcon } from "../../assets/cloud--error.icon.svg";
import { Loader } from "../../components/loader/loader.comp";
import { LoaderStep } from "../../common/enums/loader-step.enum";
import { KnowledgeBase } from "../../common/interfaces/admin/knowledge-base.interface";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { UserRole } from "../../common/enums/user-role.enum";
import { isMobileDevice } from "../../common/utilities/platform/platform.util";
import {
    KnowledgeBaseMenu,
    KnowledgeBaseMenuItem,
    KnowledgeBaseMenuItemType
} from "../../components/knowledge-base-menu/knowledge-base-menu.comp";
import { ModalTemplate } from "../../components/modal-template/modal-template.comp";
import AddKnowledgeBaseView from "../admin/add-knowledge-base/add-knowledge-base";

const KnowledgeBaseUnavailableView = () => {
    const {
        isSuperAdmin,
        user,
        knowledgeBases,
        loadKnowledgeBases,
        updateUserKnowledgeBase,
        logout
    } = FirebaseAuthContainer.useContainer();
    const [kbList, setKBList] = useState<KnowledgeBaseMenuItem[]>([]);
    const [step, setStep] = useState<LoaderStep>(LoaderStep.start);
    const [selectedKB, setSelectedKB] = useState<string>();
    const [addKBModalOpen, setAddKBModalOpen] = useState<boolean>(false);
    const [description] = useState<string>(
        knowledgeBases.length > 0
            ? "The Assistant you're trying to access is no longer available. There might be several reasons why: your access has been revoked or this Assistant has been deleted. Please select another Assistant to proceed."
            : "The Assistant you're trying to access is no longer available. There might be several reasons why: your access has been revoked or this Assistant has been deleted. Please contact support team."
    );

    const handleAddKBModalOpen = () => {
        setAddKBModalOpen(true);
    };

    const handleAddKBModalClose = () => {
        setAddKBModalOpen(false);
    };

    const handleAddKBClick = useCallback(() => {
        if (!isMobileDevice()) {
            handleAddKBModalOpen();
        }
    }, []);

    const handleAddKBSuccess = (kb: KnowledgeBase) => {
        if (!user) return;

        loadKnowledgeBases(user.companyId).then(() => {
            updateUserKnowledgeBase(kb.uid);
        });
    };

    useEffect(() => {
        setKBList(
            knowledgeBases.map(kb => ({
                type: KnowledgeBaseMenuItemType.stroke,
                icon: CubeIcon,
                title: kb.displayName,
                role: kb.role,
                kbId: kb.uid,
                logoUrl: kb.logoUrl,
                action: () => {
                    setSelectedKB(kb.uid);
                }
            }))
        );
    }, [knowledgeBases, updateUserKnowledgeBase]);

    const handleProceedClick = () => {
        if (!user?.companyId || !selectedKB) return;

        setStep(LoaderStep.loading);
        updateUserKnowledgeBase(selectedKB);
    };

    const handleLogoutClick = () => {
        logout();
    };

    return (
        <div className="kb-unavailable-background">
            <div className="kb-unavailable-view">
                {step === LoaderStep.start && (
                    <div className="kb-unavailable-title">
                        <SvgIcon
                            className="kb-unavailable-title__icon"
                            component={CloudErrorIcon}
                            inheritViewBox
                        />
                        <span className="kb-unavailable-title__text">Assistant Unavailable</span>
                    </div>
                )}
                {step === LoaderStep.loading && (
                    <div className="kb-unavailable-title kb-unavailable-title--loading">
                        <Loader size={40} borderWidth={5} />
                        <span className="kb-unavailable-title__text">Processing</span>
                    </div>
                )}
                <div className="kb-unavailable-description">
                    <span className="kb-unavailable-description__text">{description}</span>
                </div>
                {kbList.length > 0 && (
                    <div className="kb-unavailable-data">
                        <KnowledgeBaseMenu
                            items={kbList}
                            currentKnowledgeBaseId={selectedKB}
                            addAssistantAction={
                                isSuperAdmin || user?.role === UserRole.admin
                                    ? handleAddKBClick
                                    : undefined
                            }
                        />
                    </div>
                )}
                <div className="kb-unavailable-actions">
                    {kbList.length > 0 ? (
                        <Button
                            className="primary-button"
                            variant="text"
                            onClick={handleProceedClick}
                            disabled={!selectedKB || step === LoaderStep.loading}
                        >
                            {step === LoaderStep.start ? "Proceed" : "Please wait"}
                        </Button>
                    ) : (
                        <Button
                            className="primary-button"
                            variant="text"
                            onClick={handleLogoutClick}
                        >
                            Log Out
                        </Button>
                    )}
                </div>
            </div>
            <ModalTemplate
                open={addKBModalOpen}
                width={600}
                handleClose={handleAddKBModalClose}
                ariaLabel="Add Assistant"
            >
                <AddKnowledgeBaseView
                    handleClose={handleAddKBModalClose}
                    handleSuccess={handleAddKBSuccess}
                />
            </ModalTemplate>
        </div>
    );
};

export default KnowledgeBaseUnavailableView;
